import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import type {
  OutfitReview,
  ProfileSuggestion,
  ProfileVertical,
  RankingSequenceItem,
  RatingResult,
} from '@photofeeler/shared';
import {
  ANALYSIS_TIMEOUT_MS,
  CONTEXT_OPTIONS,
  PROFILE_VERTICAL_CONFIGS,
  getProjectIdForVertical,
  getRankingSequenceRecommendation,
  getModelAgreementConfidence,
  getWinnerConfidence,
  normalizeProfileVertical,
} from '@photofeeler/shared';
import { API_CONFIG } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../components/Toast';
import { useUserStore } from '../stores/user';
import { useGameStore } from '../stores/game';
import { ResultsEditorial } from '../components/pretext';

interface Improvement {
  category: 'fix' | 'retake';
  issue: string;
  detail: string;
  suggestion: string;
  impact: 'high' | 'medium' | 'low';
}

interface RawResponse {
  suggestions?: string[];
  bestUseCase?: string;
  attributes?: Record<string, number>;
  photoAnalysis?: string;
  strengths?: string[];
  improvements?: Improvement[];
  profileSuggestions?: ProfileSuggestion[];
  outfitReview?: OutfitReview;
}

interface RatingsResponse {
  photoId: string;
  ratings: { model: string; score: number; summary: string; traits: string[]; raw_response?: RawResponse }[];
  aggregate: { composite_score: number; context: string; model_count: number } | null;
  status: string;
  failureReason?: string | null;
}

// Blog post links by context
const BLOG_LINKS: Record<string, { slug: string; title: string }> = {
  dating: { slug: 'dating-photos-that-get-matches', title: 'The 5-Photo Formula That Gets 3x More Matches' },
  business: { slug: 'linkedin-headshot-guide', title: 'What Recruiters Actually See in Your LinkedIn Photo' },
  social: { slug: 'instagram-photos-that-perform', title: 'Why Your Instagram Photos Get Ignored (And How to Fix It)' },
};

function formatSuggestionArea(area: ProfileSuggestion['area']): string {
  return area.replace('_', ' ');
}

function aggregateOutfitReview(reviews: OutfitReview[]): OutfitReview | null {
  if (reviews.length === 0) return null;

  const signalCounts = reviews.reduce<Record<OutfitReview['signal'], number>>(
    (counts, review) => {
      counts[review.signal] += 1;
      return counts;
    },
    { strong: 0, mixed: 0, needs_work: 0 }
  );

  const signal = (Object.entries(signalCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ||
    reviews[0].signal) as OutfitReview['signal'];
  const summary = reviews.find(review => review.signal === signal)?.summary || reviews[0].summary;
  const seen = new Set<string>();
  const recommendations = reviews
    .flatMap(review => review.recommendations)
    .filter(recommendation => {
      const key = `${recommendation.title}:${recommendation.suggestion}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.priority] - order[b.priority];
    })
    .slice(0, 4);

  return {
    signal,
    summary,
    recommendations,
  };
}

interface RetakeOfRatings {
  composite: number;
  attributes: Record<string, number>;
  modelCount: number;
}

export function Results() {
  const { photoId } = useParams<{ photoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const retakeOf = (location.state as { retakeOf?: string } | null)?.retakeOf;
  const { session } = useAuth();
  const { setCredits } = useUserStore();
  const {
    projects,
    outcomeLogs,
    retestLogs,
    recordPhotoAnalysis,
    recordRetest,
    applyProjectRecommendations,
    logOutcome,
  } = useGameStore();
  const [ratings, setRatings] = useState<RatingResult[]>([]);
  const [rawResponses, setRawResponses] = useState<Record<string, RawResponse>>({});
  const [composite, setComposite] = useState<number | null>(null);
  const [context, setContext] = useState('');
  const [modelCount, setModelCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [coachingTab, setCoachingTab] = useState<'strengths' | 'improve'>('strengths');
  const [retakeOfRatings, setRetakeOfRatings] = useState<RetakeOfRatings | null>(null);
  const [retakeComparisonDismissed, setRetakeComparisonDismissed] = useState(false);
  const [showOutcomePrompt, setShowOutcomePrompt] = useState(true);
  const [showOutcomeForm, setShowOutcomeForm] = useState(false);
  const [outcomeValue, setOutcomeValue] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const pollingRef = useRef(true); const doneRef = useRef(false); const cleanupRef = useRef<(() => void) | null>(null);
  const projectLoggedRef = useRef<string | null>(null);
  const retakeComparisonTrackedRef = useRef(false);

  const syncCredits = useCallback(async () => {
    if (!session?.access_token) return;

    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/user/credits`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const data = await res.json() as { credits: number };
      setCredits(Math.max(0, data.credits));
    } catch {
      // Best-effort sync after failures; ignore if it cannot be refreshed.
    }
  }, [session?.access_token, setCredits]);

  const fetchResults = useCallback(async () => {
    if (!photoId || !session?.access_token || doneRef.current) return;
    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/ratings/${photoId}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.status === 404) return;
      if (!res.ok) throw new Error('Failed');
      const data = await res.json() as RatingsResponse;
      const raws: Record<string, RawResponse> = {};
      setRatings(data.ratings.map(r => {
        if (r.raw_response) raws[r.model] = r.raw_response;
        return {
          model: r.model, overallScore: r.score, attributes: r.raw_response?.attributes || {},
          summary: r.summary, suggestions: r.raw_response?.suggestions || [],
          bestUseCase: r.raw_response?.bestUseCase || '', traits: r.traits || [],
          context: (data.aggregate?.context || 'dating') as RatingResult['context'],
        };
      }));
      setRawResponses(raws);
      if (data.aggregate) {
        setComposite(data.aggregate.composite_score);
        setContext(data.aggregate.context);
        setModelCount(data.aggregate.model_count);
        pollingRef.current = false;
        doneRef.current = true;
        const agg = data.aggregate;
        if (agg) {
          import('../lib/analytics').then(({ trackResultsViewed }) =>
            trackResultsViewed(agg.composite_score, agg.model_count, agg.context)
          );
        }

        if (projectLoggedRef.current !== photoId) {
          const vertical = normalizeProfileVertical(data.aggregate.context);
          const projectId = getProjectIdForVertical(vertical);
          const project = projects.find(item => item.id === projectId);
          const profileSuggestions = Array.from(
            new Set(
              Object.values(raws).flatMap(raw =>
                (raw.profileSuggestions || []).map(
                  suggestion =>
                    `${formatSuggestionArea(suggestion.area)}: ${suggestion.suggestion}`
                )
              )
            )
          );
          const outfitReview = aggregateOutfitReview(
            Object.values(raws).flatMap(raw => (raw.outfitReview ? [raw.outfitReview] : []))
          );
          const polishChecklist = Array.from(
            new Set([
              ...profileSuggestions,
              ...(outfitReview?.recommendations.map(recommendation => recommendation.suggestion) || []),
            ])
          ).slice(0, 4);

          if (project?.currentScore != null) {
            recordRetest({
              vertical,
              photoId,
              score: data.aggregate.composite_score,
            });
          } else {
            recordPhotoAnalysis({
              vertical,
              photoId,
              score: data.aggregate.composite_score,
            });
          }

          applyProjectRecommendations(projectId, {
            profileSuggestions,
            polishChecklist,
            ...(outfitReview ? { outfitReview } : {}),
          });

          projectLoggedRef.current = photoId;
        }
      }
      if (data.status === 'failed') {
        setError(data.failureReason || 'Analysis failed. Credit refunded.');
        pollingRef.current = false;
        doneRef.current = true;
        void syncCredits();
      }
    } catch { /* keep polling */ }
  }, [applyProjectRecommendations, photoId, projects, recordPhotoAnalysis, recordRetest, session?.access_token, syncCredits]);

  useEffect(() => {
    if (!photoId) { navigate('/app'); return; }
    pollingRef.current = true; doneRef.current = false;

    fetchResults().then(() => {
      if (doneRef.current) return;
      const i = setInterval(() => { if (pollingRef.current) fetchResults(); }, 2000);
      const t = setTimeout(() => { if (pollingRef.current && !doneRef.current) { pollingRef.current = false; setError('Taking longer than expected. Credit will be refunded if incomplete.'); } }, ANALYSIS_TIMEOUT_MS);
      cleanupRef.current = () => { clearInterval(i); clearTimeout(t); pollingRef.current = false; };
    });

    return () => { pollingRef.current = false; cleanupRef.current?.(); };
  }, [photoId, navigate, fetchResults]);

  // Fetch photo thumbnail immediately (not waiting for composite)
  useEffect(() => {
    if (!photoId || !session?.access_token) return;
    let revoke = '';
    fetch(`${API_CONFIG.RATE_URL}/photos/${photoId}/image`, {
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    }).then(res => res.ok ? res.blob() : null).then(blob => {
      if (blob) { revoke = URL.createObjectURL(blob); setThumbUrl(revoke); }
    }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [photoId, session?.access_token, composite]);

  // Fetch original photo ratings when this is a retake
  useEffect(() => {
    if (!retakeOf || !session?.access_token) return;
    let cancelled = false;

    fetch(`${API_CONFIG.RATE_URL}/ratings/${retakeOf}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(res => (res.ok ? (res.json() as Promise<RatingsResponse>) : null))
      .then(data => {
        if (cancelled || !data || !data.aggregate) return;
        const attrs: Record<string, number> = {};
        for (const r of data.ratings) {
          if (r.raw_response?.attributes) {
            for (const [k, v] of Object.entries(r.raw_response.attributes)) {
              if (attrs[k] !== undefined) attrs[k] = (attrs[k] + v) / 2;
              else attrs[k] = v;
            }
          }
        }
        setRetakeOfRatings({
          composite: data.aggregate.composite_score,
          attributes: attrs,
          modelCount: data.aggregate.model_count,
        });
      })
      .catch(() => {
        // If the original photo is gone or inaccessible, silently skip comparison
      });

    return () => { cancelled = true; };
  }, [retakeOf, session?.access_token]);

  const scoreColor = (s: number) => s >= 80 ? 'text-emerald-600' : s >= 60 ? 'text-blue-600' : s >= 40 ? 'text-amber-600' : 'text-rose';
  const scoreBg = (s: number) => s >= 80 ? 'bg-emerald-50' : s >= 60 ? 'bg-blue-50' : s >= 40 ? 'bg-amber-50' : 'bg-rose-faint';


  // Collect all attributes across ratings, sorted by value (strengths first)
  const allAttributes: [string, number][] = [];
  for (const r of ratings) {
    if (r.attributes) {
      for (const [k, v] of Object.entries(r.attributes)) {
        const existing = allAttributes.find(([name]) => name === k);
        if (existing) existing[1] = (existing[1] + v) / 2;
        else allAttributes.push([k, v]);
      }
    }
  }
  allAttributes.sort((a, b) => b[1] - a[1]);
  const strengths = allAttributes.filter(([, v]) => v >= 7.0);

  // SVG-based share card — works reliably across browsers
  const downloadShareCard = (format: '1:1' | '9:16' = '1:1') => {
    const w = format === '9:16' ? 1080 : 1080;
    const h = format === '9:16' ? 1920 : 1080;
    const ctxName = CONTEXT_OPTIONS.find((c: { value: string }) => c.value === context)?.label || context;
    if (composite === null) return;
    const sc = composite;
    const color = sc >= 80 ? '#059669' : sc >= 60 ? '#2563eb' : sc >= 40 ? '#d97706' : '#D4736A';
    const label = sc >= 80 ? 'Excellent photo' : sc >= 60 ? 'Good photo' : sc >= 40 ? 'Room to improve' : 'Try a different shot';
    const topTraits = strengths.slice(0, 3).map(([n, v]) => `${n} ${v.toFixed(1)}`).join('  ·  ');

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect width="${w}" height="${h}" fill="#FAFAF8"/>
      <text x="${w/2}" y="${format === '9:16' ? 400 : 200}" text-anchor="middle" font-family="Georgia,serif" font-size="32" fill="#999" letter-spacing="4">PHOTOFEELER AI · ${ctxName.toUpperCase()}</text>
      <text x="${w/2}" y="${format === '9:16' ? 700 : 500}" text-anchor="middle" font-family="Georgia,serif" font-size="${format === '9:16' ? 280 : 220}" font-weight="600" fill="${color}">${sc}</text>
      <text x="${w/2}" y="${format === '9:16' ? 800 : 580}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="40" font-weight="500" fill="#1A1A1A">${label}</text>
      ${topTraits ? `<text x="${w/2}" y="${format === '9:16' ? 920 : 660}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="28" fill="#059669">${topTraits}</text>` : ''}
      <text x="${w/2}" y="${h - 80}" text-anchor="middle" font-family="system-ui,sans-serif" font-size="24" fill="#aaa">Rated by ${modelCount} AI models · photofeeler.ai</text>
    </svg>`;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `photofeeler-${sc}-${format === '9:16' ? 'story' : 'card'}.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    showToast(`${format === '9:16' ? 'Stories card' : 'Share card'} downloaded!`, 'success');
  };

  if (error) return (
    <div className="pt-28 pb-16 text-center max-w-4xl mx-auto px-5 sm:px-8 animate-fade-in">
      <div className="max-w-sm mx-auto">
        <h2 className="font-sans text-2xl text-ink mb-3">We hit a snag</h2>
        <p className="text-sm text-ink-faint mb-8" style={{fontWeight:300}}>{error}</p>
        <button onClick={() => navigate('/app')} className="btn-primary text-xs">Back</button>
      </div>
    </div>
  );

  if (composite === null) {
    const steps = [
      'Evaluating facial symmetry and expression...',
      'Analyzing ambient lighting contrast...',
      'Cross-referencing against 47,000+ rated photos...',
      'Measuring trustworthiness and likability signals...',
      'Generating personalized coaching feedback...',
    ];
    const modelsCompleted = ratings.length;
    const progress = Math.max(5, Math.min(95, (modelsCompleted / 3) * 80 + 10));
    const stepsCompleted = modelsCompleted === 0 ? 1 : modelsCompleted >= 3 ? 4 : modelsCompleted + 1;

    const tips = [
      'Photos with natural window light score 10-15 points higher on average.',
      'A genuine smile that reaches your eyes (Duchenne smile) boosts trustworthiness by 20%.',
      'Profiles with a full-body photo get 45% more matches on dating apps.',
      'The best LinkedIn photos have your face filling 60% of the frame.',
      'Photos taken outdoors during golden hour consistently score highest on social media.',
      'Eye contact with the camera increases likability scores significantly.',
    ];
    const tipIndex = Math.floor(Date.now() / 5000) % tips.length;

    return (
      <div className="pt-28 pb-16 text-center max-w-md mx-auto px-5 sm:px-8 animate-fade-in">
        {/* Photo preview during analysis — larger with scanning animation */}
        {thumbUrl && (
          <div className="relative w-32 h-32 mx-auto mb-6 rounded-2xl overflow-hidden border border-surface-border shadow-card">
            <img src={thumbUrl} alt="Analyzing" className="w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute left-0 right-0 h-8 bg-gradient-to-b from-rose/20 to-transparent scan-line" />
            </div>
          </div>
        )}

        <div className="w-10 h-10 border-2 border-ink/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
        <h2 className="font-sans text-2xl text-ink mb-8">Analyzing your photo...</h2>

        <div className="text-left space-y-3 mb-8">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-3 text-sm font-sans">
              {i < stepsCompleted ? <span className="text-emerald-500 text-xs">&#10003;</span>
                : i === stepsCompleted ? <div className="w-3 h-3 border-2 border-rose/40 rounded-full animate-pulse" />
                : <div className="w-3 h-3 border border-surface-border rounded-full" />}
              <span className={i < stepsCompleted ? 'text-ink' : i === stepsCompleted ? 'text-ink-muted' : 'text-ink-faint'} style={{fontWeight: i <= stepsCompleted ? 400 : 300}}>{s}</span>
            </div>
          ))}
        </div>

        {/* Progress bar — smoother transitions */}
        <div className="h-1 bg-surface-border rounded-full overflow-hidden mb-8">
          <div className="h-full bg-rose/50 rounded-full transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} />
        </div>

        {/* Rotating tip */}
        <div className="card p-4 text-left">
          <div className="text-[10px] font-sans text-rose tracking-wider uppercase mb-1 font-medium">Did you know?</div>
          <p className="text-xs text-ink-muted leading-relaxed" style={{fontWeight: 300}}>{tips[tipIndex]}</p>
        </div>
      </div>
    );
  }

  const ctxLabel = CONTEXT_OPTIONS.find((c: { value: string; label: string }) => c.value === context)?.label || context;
  const blogLink = BLOG_LINKS[context];
  const projectVertical = normalizeProfileVertical(context);
  const projectConfig = PROFILE_VERTICAL_CONFIGS[projectVertical];
  const projectId = getProjectIdForVertical(projectVertical);
  const project = projects.find(item => item.id === projectId);
  const projectOutcomeCount = outcomeLogs.filter(log => log.projectId === projectId).length;
  const projectRetestCount = retestLogs.filter(log => log.projectId === projectId).length;
  const openProjectActions = project?.nextActions.filter(action => !action.completed).slice(0, 3) || [];
  const profileSuggestions = Array.from(
    new Map(
      Object.values(rawResponses)
        .flatMap(raw => raw.profileSuggestions || [])
        .map(suggestion => [`${suggestion.area}:${suggestion.suggestion}`, suggestion] as const)
    ).values()
  );
  const outfitReview = aggregateOutfitReview(
    Object.values(rawResponses).flatMap(raw => (raw.outfitReview ? [raw.outfitReview] : []))
  );
  const relatedCompareRun =
    project?.compareRuns
      .slice()
      .reverse()
      .find(run => run.rankings.some(ranking => ranking.photoId === photoId)) ||
    project?.compareRuns.at(-1) ||
    null;
  const rankingSequence = relatedCompareRun
    ? getRankingSequenceRecommendation(projectVertical, relatedCompareRun.rankings)
    : null;
  const rankingPosition = rankingSequence?.items.find(item => item.photoId === photoId) ?? null;
  const rankingConfidence = relatedCompareRun ? getWinnerConfidence(relatedCompareRun.rankings) : null;
  const modelAgreement = getModelAgreementConfidence(ratings.map(rating => rating.overallScore));
  const verdictConfidence = rankingConfidence ?? modelAgreement;

  // Outcome logging: detect whether the current score beats the previous best
  const latestRetestForPhoto = retestLogs
    .filter(log => log.projectId === projectId && log.photoId === photoId)
    .at(-1);
  const previousBest =
    latestRetestForPhoto && latestRetestForPhoto.previousScore != null
      ? latestRetestForPhoto.previousScore
      : null;
  const scoreImproved = previousBest != null && composite > previousBest;

  const saveOutcome = () => {
    const val = parseInt(outcomeValue, 10);
    if (isNaN(val) || val < 0) {
      showToast('Enter a valid number.', 'error');
      return;
    }
    const metric = projectVertical === 'dating' ? 'matches' : 'profile_views';
    logOutcome(projectId, metric, val, outcomeNotes || undefined);
    import('../lib/analytics').then(({ trackOutcomeLogged }) => trackOutcomeLogged(metric, val, context));
    showToast('Results logged! Keep tracking to see your progress.', 'success');
    setShowOutcomeForm(false);
    setShowOutcomePrompt(false);
    setOutcomeValue('');
    setOutcomeNotes('');
  };

  // Hoist coaching data so ResultsEditorial and the coaching tabs can share it
  const allStrengthsList: string[] = [];
  const allImprovements: Improvement[] = [];
  const allPhotoAnalyses: string[] = [];
  for (const r of ratings) {
    const raw = rawResponses[r.model];
    if (raw?.strengths) allStrengthsList.push(...raw.strengths);
    if (raw?.improvements) allImprovements.push(...raw.improvements);
    if (raw?.photoAnalysis) allPhotoAnalyses.push(raw.photoAnalysis);
  }
  const uniqueStrengths = [...new Set(allStrengthsList)];
  const impactOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const sortedImprovements = [...allImprovements].sort((a, b) => (impactOrder[a.impact] || 2) - (impactOrder[b.impact] || 2));
  const seenIssues = new Set<string>();
  const dedupedImprovements = sortedImprovements.filter(imp => {
    if (seenIssues.has(imp.issue)) return false;
    seenIssues.add(imp.issue);
    return true;
  });
  const hasCoaching = uniqueStrengths.length > 0 || dedupedImprovements.length > 0;
  const topHighImpactImprovement = dedupedImprovements.find(imp => imp.impact === 'high') ?? dedupedImprovements[0] ?? null;

  const verdict = getVerticalVerdict({
    vertical: projectVertical,
    score: composite,
    rankingPosition,
  });

  // Vertical-specific score interpretation label
  const scoreInterpretation = projectVertical === 'linkedin'
    ? composite >= 80
      ? 'Professional impression strength: Top tier'
      : composite >= 60
        ? 'Professional impression strength: Strong'
        : composite >= 40
          ? 'Professional impression strength: Moderate'
          : 'Professional impression strength: Needs work'
    : composite >= 80
      ? 'Lead photo potential: Top tier'
      : composite >= 60
        ? 'Lead photo potential: Strong'
        : composite >= 40
          ? 'Lead photo potential: Moderate'
          : 'Lead photo potential: Needs work';

  return (
    <div className="relative overflow-hidden pt-24 pb-16 max-w-5xl mx-auto px-5 sm:px-8 animate-fade-in">
      <div className="page-orb left-[-5rem] top-28 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-5rem] top-24 h-80 w-80 bg-info/10" />
      {/* Sticky top action bar — always visible */}
      <div className="fixed top-0 left-0 right-0 z-40 px-4 pt-3">
        <div className="app-shell-panel max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/app')} className="text-xs font-sans text-ink-muted hover:text-ink transition-colors">&larr; Back</button>
          <button onClick={() => navigate('/app')} className="btn-rose text-xs px-4 py-2">{projectVertical === 'linkedin' ? 'Upload another headshot' : 'Upload another dating photo'}</button>
        </div>
      </div>

      <div className="page-hero-panel mb-8">
        <div className="grid md:grid-cols-[180px_1fr] gap-6 items-center">
          {thumbUrl ? (
            <img src={thumbUrl} alt="Analyzed photo" className="w-full md:w-44 md:h-auto h-auto max-h-72 rounded-[28px] object-contain border border-white/60 shadow-card" />
          ) : (
            <div className="h-56 md:h-44 rounded-[28px] border border-surface-border bg-white/60" />
          )}
          <div>
            <div className="premium-kicker mb-3">{projectConfig.label} results</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink mb-3" style={{ fontWeight: 600 }}>Your photo results</h1>
            <p className="hero-copy max-w-2xl">
              Context: {ctxLabel}. This run updates your workspace, keeps the strongest recommendations, and gives you the clearest next actions to test.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <div className="premium-stat">
                <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{composite}</div>
                <div className="text-xs text-ink-faint mt-1">Composite score</div>
              </div>
              <div className="premium-stat">
                <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{modelCount}</div>
                <div className="text-xs text-ink-faint mt-1">Models used</div>
              </div>
              <div className="premium-stat">
                <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{projectOutcomeCount}</div>
                <div className="text-xs text-ink-faint mt-1">Logged outcomes</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Before/After comparison for retakes */}
      {retakeOf && retakeOfRatings && composite !== null && !retakeComparisonDismissed && (() => {
        const beforeScore = retakeOfRatings.composite;
        const afterScore = composite;
        const delta = afterScore - beforeScore;
        if (!retakeComparisonTrackedRef.current) {
          retakeComparisonTrackedRef.current = true;
          import('../lib/analytics').then(({ trackRetakeComparison }) =>
            trackRetakeComparison(beforeScore, afterScore, delta)
          );
        }
        const improved = delta > 0;
        const same = delta === 0;

        // Compare attributes between old and new
        const oldAttrs = retakeOfRatings.attributes;
        const newAttrs: Record<string, number> = {};
        for (const r of ratings) {
          if (r.attributes) {
            for (const [k, v] of Object.entries(r.attributes)) {
              if (newAttrs[k] !== undefined) newAttrs[k] = (newAttrs[k] + v) / 2;
              else newAttrs[k] = v;
            }
          }
        }

        const changes: { name: string; improved: boolean; delta: number }[] = [];
        const allKeys = new Set([...Object.keys(oldAttrs), ...Object.keys(newAttrs)]);
        for (const key of allKeys) {
          const oldVal = oldAttrs[key] ?? 0;
          const newVal = newAttrs[key] ?? 0;
          const attrDelta = newVal - oldVal;
          if (Math.abs(attrDelta) > 0.5) {
            changes.push({ name: key, improved: attrDelta > 0, delta: attrDelta });
          }
        }
        changes.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
        const topChanges = changes.slice(0, 4);

        const borderColor = improved ? 'border-emerald-200' : same ? 'border-blue-200' : 'border-amber-200';
        const bgTint = improved ? 'bg-emerald-50/50' : same ? 'bg-blue-50/50' : 'bg-amber-50/50';
        const deltaBg = improved ? 'bg-emerald-100 text-emerald-700' : same ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700';

        return (
          <div className={`card mb-10 border ${borderColor} ${bgTint} animate-fade-in`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${improved ? 'bg-emerald-100' : same ? 'bg-blue-100' : 'bg-amber-100'}`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={improved ? 'text-emerald-600' : same ? 'text-blue-600' : 'text-amber-600'}>
                    {improved ? (
                      <><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></>
                    ) : same ? (
                      <><line x1="5" y1="12" x2="19" y2="12" /></>
                    ) : (
                      <><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></>
                    )}
                  </svg>
                </div>
                <h3 className="font-sans text-lg text-ink" style={{ fontWeight: 600 }}>
                  {improved ? 'Improvement' : same ? 'Same score' : 'Score comparison'}
                </h3>
              </div>
              <button
                onClick={() => setRetakeComparisonDismissed(true)}
                className="text-ink-faint hover:text-ink transition-colors text-xs p-1"
                aria-label="Dismiss comparison"
              >
                &#x2715;
              </button>
            </div>

            {/* Score comparison */}
            <div className="flex items-center justify-center gap-4 sm:gap-8 mb-6">
              <div className="text-center">
                <div className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-2">Before</div>
                <div className={`text-4xl sm:text-5xl font-sans ${scoreColor(beforeScore)}`} style={{ fontWeight: 600 }}>
                  {beforeScore}
                </div>
                <div className="mt-2 h-2 w-24 sm:w-32 bg-surface-border rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${beforeScore >= 80 ? 'bg-emerald-400' : beforeScore >= 60 ? 'bg-blue-400' : beforeScore >= 40 ? 'bg-amber-400' : 'bg-rose/60'}`}
                    style={{ width: `${beforeScore}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-col items-center gap-1">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ink-faint">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </div>

              <div className="text-center">
                <div className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-2">After</div>
                <div className={`text-4xl sm:text-5xl font-sans ${scoreColor(afterScore)}`} style={{ fontWeight: 600 }}>
                  {afterScore}
                </div>
                <div className="mt-2 h-2 w-24 sm:w-32 bg-surface-border rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${afterScore >= 80 ? 'bg-emerald-400' : afterScore >= 60 ? 'bg-blue-400' : afterScore >= 40 ? 'bg-amber-400' : 'bg-rose/60'}`}
                    style={{ width: `${afterScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Delta badge */}
            <div className="flex justify-center mb-6">
              <div className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-sans font-medium ${deltaBg}`}>
                {improved ? '+' : ''}{delta} point{Math.abs(delta) !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Encouragement copy */}
            <p className="text-sm text-ink-muted text-center mb-6 leading-relaxed" style={{ fontWeight: 300 }}>
              {improved
                ? delta >= 15
                  ? 'Major improvement! Your changes made a real difference.'
                  : 'Nice progress. The adjustments are paying off.'
                : same
                  ? 'Same score \u2014 try a different change next time. Lighting, background, or expression are good places to start.'
                  : 'Not every retake improves \u2014 but now you know what doesn\u2019t work. Use the coaching tips below for your next attempt.'}
            </p>

            {/* What changed */}
            {topChanges.length > 0 && (
              <div className="mb-6">
                <div className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-3">What changed</div>
                <div className="space-y-2">
                  {topChanges.map(change => (
                    <div key={change.name} className="flex items-center gap-2 text-sm font-sans">
                      <span className={change.improved ? 'text-emerald-500' : 'text-amber-500'}>
                        {change.improved ? '\u2713' : '\u2717'}
                      </span>
                      <span className="text-ink capitalize" style={{ fontWeight: 400 }}>{change.name}</span>
                      <span className={`text-xs ${change.improved ? 'text-emerald-600' : 'text-amber-600'}`}>
                        ({change.improved ? '+' : ''}{change.delta.toFixed(1)})
                      </span>
                      <span className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>
                        {change.improved ? 'improved' : 'still needs work'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate('/upload', { state: { preselectedVertical: projectVertical, retakeOf: photoId } })}
                className="btn-rose text-xs"
              >
                Upload another retake
              </button>
              <button
                onClick={() => setRetakeComparisonDismissed(true)}
                className="btn-secondary text-xs"
              >
                I&apos;m happy with this
              </button>
            </div>

            {/* Model-count mismatch note */}
            {retakeOfRatings.modelCount !== modelCount && (
              <p className="text-[11px] text-ink-faint mt-2" style={{ fontWeight: 300 }}>
                Note: Original used {retakeOfRatings.modelCount} model{retakeOfRatings.modelCount !== 1 ? 's' : ''}, this analysis used {modelCount}. Scores may not be directly comparable.
              </p>
            )}
          </div>
        );
      })()}

      {/* Partial-result warning banner */}
      {modelCount < 3 && (
        <div className="mb-4 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50/60 flex items-start gap-3">
          <div className="text-amber-500 mt-0.5 shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-sans font-medium text-amber-800">
              Partial result — {modelCount} of 3 models responded
            </p>
            <p className="text-xs text-amber-700 mt-0.5" style={{ fontWeight: 300 }}>
              {3 - modelCount} model{3 - modelCount > 1 ? 's' : ''} timed out. Your score may shift on a retest. Consider retesting for a more reliable result.
            </p>
          </div>
        </div>
      )}

      {/* Composite Score with reveal animation */}
      {composite !== null && (
      <div className="card-rose text-center py-14 mb-10 relative overflow-hidden lift-hover">
        <div className="text-sm font-sans text-ink-muted font-medium mb-4 animate-fade-in">Your Score</div>
        <ScoreCounter target={composite} className={`text-8xl font-sans ${scoreColor(composite)} mb-2`} />
        <div className="text-sm font-sans text-ink animate-fade-in stagger-3" style={{fontWeight:500}}>You scored {composite}</div>
        <div className="text-xs text-ink-muted mt-1 animate-fade-in stagger-3">{scoreInterpretation}</div>
        <div className="text-sm text-ink-muted mt-2 animate-fade-in stagger-4">Based on {modelCount} AI model{modelCount !== 1 ? 's' : ''}</div>
        {composite >= 80 && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-success/30" style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animation: `float ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }} />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Editorial summary — above-the-fold magazine-style coaching overview */}
      {composite !== null && (
        <ResultsEditorial
          photoUrl={thumbUrl}
          score={composite}
          verdict={verdict.title}
          confidenceLabel={verdictConfidence.label}
          strengths={uniqueStrengths.slice(0, 4)}
          topImprovement={
            topHighImpactImprovement
              ? { issue: topHighImpactImprovement.issue, suggestion: topHighImpactImprovement.suggestion }
              : null
          }
          vertical={projectVertical}
          className="mb-10"
        />
      )}

      {project && (
        <div className="card mb-10 lift-hover">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-1">
                {projectConfig.shortLabel} workspace
              </div>
              <h2 className="font-sans text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
                {project.title}
              </h2>
              <p className="text-sm text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                {project.goal}
              </p>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-ink-faint uppercase tracking-wider mb-1">Status</div>
              <div className="text-sm font-sans text-ink" style={{ fontWeight: 500 }}>
                {project.status}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 mb-5">
            {[
              { label: 'Photos', value: project.photoIds.length },
              { label: 'Best', value: project.bestScore ?? '--' },
              { label: 'Retests', value: projectRetestCount },
              { label: 'Outcomes', value: projectOutcomeCount },
            ].map(stat => (
              <div key={stat.label} className="rounded-xl border border-surface-border bg-cream-warm px-3 py-3 text-center">
                <div className="text-base font-sans text-ink" style={{ fontWeight: 600 }}>
                  {stat.value}
                </div>
                <div className="text-[10px] text-ink-faint">{stat.label}</div>
              </div>
            ))}
          </div>

          {openProjectActions.length > 0 && (
            <div>
              <div className="text-sm font-sans font-medium text-ink-muted mb-3">Next actions</div>
              <div className="space-y-3">
                {openProjectActions.map(action => (
                  <div key={action.id} className="flex items-start gap-3">
                    <span className="text-ink-faint mt-0.5 text-xs">○</span>
                    <div>
                      <div className="text-sm font-sans font-medium text-ink">{action.title}</div>
                      <div className="text-xs text-ink-faint mt-0.5" style={{ fontWeight: 300 }}>
                        {action.detail}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-10">
        <div className="card lift-hover">
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
            {projectVertical === 'linkedin' ? 'Headshot verdict' : 'Lead-photo verdict'}
          </div>
          <h2 className="font-sans text-xl text-ink mb-2" style={{ fontWeight: 600 }}>
            {verdict.title}
          </h2>
          <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
            {verdict.detail}
          </p>
          <div className="flex flex-wrap gap-2">
            <div className="rounded-full border border-surface-border bg-cream-warm px-3 py-1 text-[11px] font-sans text-ink">
              {projectVertical === 'linkedin' ? 'Use case: LinkedIn profile' : 'Use case: dating apps'}
            </div>
            {rankingPosition && (
              <div className="rounded-full border border-surface-border bg-cream-warm px-3 py-1 text-[11px] font-sans text-ink">
                {projectVertical === 'linkedin' ? rankingPosition.title : `${rankingPosition.title} slot`}
              </div>
            )}
            <div className="rounded-full border border-surface-border bg-cream-warm px-3 py-1 text-[11px] font-sans text-ink">
              Score band: {composite >= 80 ? 'Excellent' : composite >= 60 ? 'Strong' : composite >= 40 ? 'Usable' : 'Weak'}
            </div>
          </div>
        </div>

        <div className="card lift-hover">
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
            {rankingConfidence ? 'Decision confidence' : 'Model agreement'}
          </div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <h2 className="font-sans text-xl text-ink" style={{ fontWeight: 600 }}>
              {verdictConfidence.label}
            </h2>
            <div className={`rounded-full px-3 py-1 text-[11px] font-sans ${
              verdictConfidence.level === 'high'
                ? 'bg-emerald-50 text-emerald-700'
                : verdictConfidence.level === 'medium'
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-amber-50 text-amber-700'
            }`}>
              {rankingConfidence ? `Gap ${verdictConfidence.scoreGap}` : `Spread ${verdictConfidence.scoreGap}`}
            </div>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
            {verdictConfidence.detail}
          </p>
          <div className="rounded-2xl border border-surface-border bg-cream-warm px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-faint mb-1">
              {rankingConfidence ? 'What this means' : 'How to use it'}
            </div>
            <div className="text-sm text-ink-muted" style={{ fontWeight: 300 }}>
              {rankingConfidence
                ? projectVertical === 'linkedin'
                  ? 'If the gap stays clear across a few options, ship the winner to LinkedIn and save the runner-up as your backup.'
                  : 'If the gap is clear, lead with the winner and stop rotating weaker openers into slot one.'
                : 'Agreement tells you how stable this score looks across models. Use compare runs when you need a stronger winner call.'}
            </div>
          </div>
        </div>
      </div>

      {/* Retention prompt — context-aware next-step nudge */}
      {(() => {
        const hasHighImpactImprovements = Object.values(rawResponses).some(
          raw => raw.improvements?.some(imp => imp.impact === 'high')
        );
        const photosForContext = project?.photoIds.length ?? 0;
        const uploadState = { preselectedVertical: projectVertical };
        const retakeState = { preselectedVertical: projectVertical, retakeOf: photoId };
        const ctxName = projectVertical === 'linkedin' ? 'LinkedIn' : 'Dating';

        // Prompt selection logic
        if (composite < 30) {
          // Too low — just encouragement, no prompt
          return null;
        }

        // Prompt 2: Retake & improve (score < 75 with high-impact improvements)
        if (composite < 75 && hasHighImpactImprovements) {
          return (
            <div className="card mb-10 lift-hover">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-sans text-lg text-ink mb-1" style={{ fontWeight: 600 }}>Retake and improve</h3>
                  <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                    Your score: {composite}. Apply the tips below, retake the photo, and upload the new version. We&apos;ll show you the improvement.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => navigate('/upload', { state: retakeState })}
                      className="btn-rose text-xs"
                    >
                      Upload retake
                    </button>
                    <span className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>1 credit</span>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Prompt 3: Backup (score 80+ with < 3 photos)
        if (composite >= 80 && photosForContext < 3) {
          return (
            <div className="card mb-10 lift-hover">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <rect x="4" y="4" width="16" height="14" rx="2" ry="2" fill="none" />
                    <rect x="6" y="1" width="12" height="14" rx="2" ry="2" fill="none" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-sans text-lg text-ink mb-1" style={{ fontWeight: 600 }}>Strong photo — now find your backup</h3>
                  <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                    {projectVertical === 'linkedin'
                      ? 'Strong headshot. Upload 1\u20132 more to build your shortlist.'
                      : 'Great lead photo. Upload more to find your #2 and #3 for a complete lineup.'}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => navigate('/shootout', { state: { vertical: projectVertical } })}
                      className="btn-rose text-xs"
                    >
                      Find my backup
                    </button>
                    <span className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>1 credit per photo</span>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Prompt 1: Compare (default for 75+ or multiple photos)
        if (composite >= 75 || photosForContext > 1) {
          return (
            <div className="card mb-10 lift-hover">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                    <rect x="2" y="2" width="8" height="20" rx="1" />
                    <rect x="14" y="2" width="8" height="20" rx="1" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-sans text-lg text-ink mb-1" style={{ fontWeight: 600 }}>Want to know which photo wins?</h3>
                  <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                    Upload 2 more photos and we&apos;ll rank your full {ctxName} lineup. Most users find their best photo isn&apos;t the one they expected.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => navigate('/upload', { state: uploadState })}
                      className="btn-rose text-xs"
                    >
                      {projectVertical === 'linkedin' ? 'Compare headshot options' : 'Compare with your lineup'}
                    </button>
                    <span className="text-xs text-ink-faint" style={{ fontWeight: 300 }}>1 credit per photo</span>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return null;
      })()}

      <div className="card mb-10 lift-hover">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
              {projectVertical === 'linkedin' ? 'Headshot shortlist' : 'Photo order recommendation'}
            </div>
            <h2 className="font-sans text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
              {rankingSequence?.title ?? (projectVertical === 'linkedin' ? 'Build a clearer shortlist' : 'Build your lineup order')}
            </h2>
            <p className="text-sm text-ink-muted" style={{ fontWeight: 300 }}>
              {rankingSequence?.subtitle ?? (
                projectVertical === 'linkedin'
                  ? 'Run a compare with at least two finished headshots to choose a primary LinkedIn photo and keep a strong backup.'
                  : 'Run a compare with at least two finished dating photos to get a real lead-photo call and a suggested lineup order.'
              )}
            </p>
          </div>
          {!rankingSequence && (
            <button
              onClick={() => {
                const photosForContext = project?.photoIds.length ?? 0;
                import('../lib/analytics').then(({ trackCompareStarted }) => trackCompareStarted(photosForContext, context));
                navigate(`/compare?vertical=${projectVertical}`);
              }}
              className="btn-secondary text-xs shrink-0"
            >
              {projectVertical === 'linkedin' ? 'Rank headshots' : 'Rank lineup'}
            </button>
          )}
        </div>

        {rankingSequence ? (
          <div className="space-y-3">
            {rankingSequence.items.map(item => (
              <div key={item.photoId} className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${
                item.photoId === photoId ? 'border-rose/30 bg-rose-faint' : 'border-surface-border bg-cream-warm'
              }`}>
                <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-sans text-ink">
                  {item.position}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>
                      {item.title}
                    </div>
                    <div className="text-[11px] font-sans text-ink-faint">
                      {item.score}
                    </div>
                  </div>
                  <p className="text-xs text-ink-faint mt-1 leading-relaxed" style={{ fontWeight: 300 }}>
                    {item.rationale}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-surface-border bg-cream-warm px-4 py-4">
            <div className="text-sm font-sans text-ink mb-1" style={{ fontWeight: 600 }}>
              {projectVertical === 'linkedin' ? 'You only have a single-photo read so far.' : 'You only have a single-photo read so far.'}
            </div>
            <p className="text-xs text-ink-faint leading-relaxed" style={{ fontWeight: 300 }}>
              {projectVertical === 'linkedin'
                ? 'A shortlist is more trustworthy than one isolated score. Compare a few headshots side by side before you lock the winner.'
                : 'A lineup order is stronger than one isolated score. Compare a few dating photos side by side before you set your first photo.'}
            </p>
          </div>
        )}
      </div>

      {(profileSuggestions.length > 0 || outfitReview) && (
        <div className="grid lg:grid-cols-2 gap-5 mb-10">
          {profileSuggestions.length > 0 && (
            <div className="card lift-hover">
              <div className="text-sm font-sans font-medium text-ink-muted mb-4">Profile upgrades</div>
              <div className="space-y-3">
                {profileSuggestions.map(suggestion => (
                  <div key={`${suggestion.area}-${suggestion.suggestion}`} className="flex items-start gap-3">
                    <span className="text-rose mt-0.5 text-xs">→</span>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-faint mb-1">
                        {formatSuggestionArea(suggestion.area)}
                      </div>
                      <div className="text-sm text-ink-muted" style={{ fontWeight: 300 }}>
                        {suggestion.suggestion}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {outfitReview && (
            <div className="card lift-hover">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-sm font-sans font-medium text-ink-muted">Outfit review</div>
                <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                  {outfitReview.signal.replace('_', ' ')}
                </div>
              </div>
              <p className="text-sm text-ink-muted mb-4" style={{ fontWeight: 300 }}>
                {outfitReview.summary}
              </p>
              <div className="space-y-3">
                {outfitReview.recommendations.map(recommendation => (
                  <div key={`${recommendation.title}-${recommendation.suggestion}`} className="rounded-xl border border-surface-border bg-cream-warm p-3">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-sm font-sans font-medium text-ink">
                        {recommendation.title}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-faint">
                        {recommendation.priority}
                      </div>
                    </div>
                    <div className="text-[11px] text-ink-faint mb-1" style={{ fontWeight: 300 }}>
                      {recommendation.detail}
                    </div>
                    <div className="text-xs text-ink">→ {recommendation.suggestion}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Exportable Status Badge — for high scorers */}
      {composite >= 80 && (
        <div className="card p-6 mb-8 text-center lift-hover">
          <div className="text-xs font-sans text-ink-muted font-medium mb-3">You earned a badge!</div>
          <div className="inline-block px-6 py-3 rounded-xl bg-success/10 border border-success/20 mb-4">
            <div className="text-lg font-sans text-success" style={{fontWeight: 700}}>Top 15%</div>
            <div className="text-xs text-ink-muted">PhotoFeeler Verified &middot; {ctxLabel}</div>
          </div>
          <div className="flex justify-center gap-3">
            <button onClick={() => {
              const sc = composite;
              const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200">
                <rect width="400" height="200" rx="16" fill="#FAFAF8"/>
                <rect x="1" y="1" width="398" height="198" rx="15" fill="none" stroke="#059669" stroke-opacity="0.2"/>
                <text x="200" y="70" text-anchor="middle" font-family="system-ui,sans-serif" font-size="36" font-weight="700" fill="#059669">Top 15%</text>
                <text x="200" y="105" text-anchor="middle" font-family="system-ui,sans-serif" font-size="16" fill="#6B6B6B">Score: ${sc} · ${ctxLabel}</text>
                <text x="200" y="155" text-anchor="middle" font-family="system-ui,sans-serif" font-size="12" fill="#999999">PhotoFeeler Verified · Rated by ${modelCount} AI models</text>
                <text x="200" y="180" text-anchor="middle" font-family="system-ui,sans-serif" font-size="11" fill="#BBBBBB">photofeeler.ai</text>
              </svg>`;
              const blob = new Blob([svg], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.download = `photofeeler-badge-${sc}.svg`;
              link.href = url;
              link.click();
              URL.revokeObjectURL(url);
              showToast('Badge downloaded!', 'success');
            }} className="btn-secondary text-xs">Download badge</button>
            <button onClick={() => {
              const text = `I scored in the Top 15% on PhotoFeeler AI! Score: ${composite}/100 for ${ctxLabel}. Get your photo rated free at photofeeler.ai`;
              if (navigator.share) {
                navigator.share({ title: 'PhotoFeeler AI Badge', text }).catch(() => {});
              } else {
                navigator.clipboard.writeText(text).then(() => showToast('Copied to clipboard!', 'success')).catch(() => showToast('Couldn\u0027t copy \u2014 try again or copy manually.', 'error'));
              }
            }} className="btn-ghost text-xs">Share to LinkedIn</button>
          </div>
        </div>
      )}

      {/* Strengths section — highlight what's working (Phase 3) */}
      {strengths.length > 0 && (
        <div className="mb-10">
          <h2 className="text-sm font-sans font-medium text-ink-muted mb-4">Your strengths</h2>
          <div className="flex flex-wrap gap-2">
            {strengths.map(([name, val]) => (
              <div key={name} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${scoreBg(val * 10)} border border-surface-border`}>
                <span className="text-sm font-sans font-medium text-ink" style={{ textTransform: 'capitalize' }}>{name}</span>
                <span className={`text-sm font-sans ${scoreColor(val * 10)}`} style={{fontWeight:600}}>{val.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Coaching Section — detailed tabs using hoisted data */}
      {hasCoaching ? (
          <div className="card mb-10 lift-hover">
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setCoachingTab('strengths')}
                className={`text-sm font-sans pb-1 border-b-2 transition-colors ${coachingTab === 'strengths' ? 'border-rose text-ink font-medium' : 'border-transparent text-ink-faint'}`}
              >
                What&apos;s working ({uniqueStrengths.length})
              </button>
              <button
                onClick={() => setCoachingTab('improve')}
                className={`text-sm font-sans pb-1 border-b-2 transition-colors ${coachingTab === 'improve' ? 'border-rose text-ink font-medium' : 'border-transparent text-ink-faint'}`}
              >
                How to improve ({dedupedImprovements.length})
              </button>
            </div>

            {coachingTab === 'strengths' ? (
              <div className="space-y-3">
                {allPhotoAnalyses[0] && (
                  <p className="text-sm text-ink-muted mb-4 leading-relaxed italic" style={{fontWeight:300}}>
                    {allPhotoAnalyses[0]}
                  </p>
                )}
                {uniqueStrengths.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-emerald-500 mt-0.5 text-xs">✓</span>
                    <span className="text-sm text-ink-muted" style={{fontWeight:300}}>{s}</span>
                  </div>
                ))}
                {uniqueStrengths.length === 0 && (
                  <p className="text-sm text-ink-faint" style={{fontWeight:300}}>No specific strengths identified — this may improve with a different photo.</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {dedupedImprovements.map((imp, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${imp.impact === 'high' ? 'border-rose/20 bg-rose-faint' : imp.impact === 'medium' ? 'border-amber-200 bg-amber-50' : 'border-surface-border bg-cream-warm'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-[10px] font-sans font-medium tracking-wider uppercase ${imp.impact === 'high' ? 'text-rose' : imp.impact === 'medium' ? 'text-amber-600' : 'text-ink-faint'}`}>
                        {imp.impact} impact
                      </span>
                      <span className="text-[10px] text-ink-faint">·</span>
                      <span className="text-[10px] font-sans text-ink-faint capitalize">{imp.category === 'fix' ? 'Can fix without retaking' : 'Needs a retake'}</span>
                    </div>
                    <div className="text-sm font-sans font-medium text-ink mb-1">{imp.issue}</div>
                    <p className="text-xs text-ink-muted mb-2" style={{fontWeight:300}}>{imp.detail}</p>
                    <p className="text-xs text-ink" style={{fontWeight:400}}>→ {imp.suggestion}</p>
                  </div>
                ))}
                {dedupedImprovements.length === 0 && (
                  <p className="text-sm text-ink-faint" style={{fontWeight:300}}>No specific improvements identified — looking great!</p>
                )}

                {/* Retake checklist */}
                {dedupedImprovements.filter(d => d.category === 'retake').length > 0 && (
                  <div className="mt-4 pt-4 border-t border-surface-border">
                    <div className="text-sm font-sans font-medium text-ink-muted mb-3">Retake checklist</div>
                    <div className="space-y-2">
                      {dedupedImprovements.filter(d => d.category === 'retake').map((d, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-ink-muted" style={{fontWeight:300}}>
                          <span className="text-ink-faint mt-0.5">□</span>
                          <span>{d.suggestion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {blogLink && (
              <div className="mt-6 pt-4 border-t border-surface-border">
                <Link to={`/blog/${blogLink.slug}`} className="text-sm text-accent hover:text-accent-strong transition-colors">
                  {blogLink.title} →
                </Link>
              </div>
            )}
          </div>
        ) : null}

      {/* Outcome Logging - shows when score improved */}
      {scoreImproved && showOutcomePrompt && !showOutcomeForm && (
        <div className="card p-6 mb-8">
          <div className="text-center mb-4">
            <div className="text-2xl mb-2">📈</div>
            <h3 className="font-serif text-lg text-ink" style={{ fontWeight: 600 }}>
              Your score improved!
            </h3>
            <p className="text-sm text-ink-muted mt-1" style={{ fontWeight: 300 }}>
              {previousBest} → {composite} (+{composite - previousBest} points)
            </p>
          </div>
          <p className="text-sm text-ink-muted text-center mb-4" style={{ fontWeight: 300 }}>
            Track whether this translates to real results.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => setShowOutcomeForm(true)}
              className="btn-rose text-sm w-full"
            >
              Log my results
            </button>
            <button
              onClick={() => setShowOutcomePrompt(false)}
              className="text-xs text-ink-faint w-full text-center"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {showOutcomeForm && (
        <div className="card p-6 mb-8">
          <h3 className="font-serif text-lg text-ink mb-4" style={{ fontWeight: 600 }}>
            How are things going?
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-1 block">
                {projectVertical === 'dating' ? 'Matches this week' : 'Profile views this week'}
              </label>
              <input
                type="number"
                min="0"
                value={outcomeValue}
                onChange={(e) => setOutcomeValue(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-surface-border bg-white/60 text-sm"
                placeholder="e.g. 12"
              />
            </div>
            <div>
              <label className="text-xs font-sans text-ink-faint uppercase tracking-wider mb-1 block">
                Notes (optional)
              </label>
              <input
                type="text"
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-surface-border bg-white/60 text-sm"
                placeholder="e.g. switched lead photo, got more replies"
              />
            </div>
            <button
              onClick={saveOutcome}
              className="btn-rose text-sm w-full"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Per-model detail cards */}
      <div className="mb-4">
        <h2 className="text-sm font-sans font-medium text-ink-muted">Per-model scores</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        {ratings.map((r: RatingResult) => {
          const sortedAttrs = r.attributes ? Object.entries(r.attributes).sort((a, b) => b[1] - a[1]) : [];
          return (
            <div key={r.model} className="card">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xs font-sans text-ink-muted">{r.model}</span>
                <span className={`text-xl font-sans ${scoreColor(r.overallScore)}`} style={{fontWeight:600}}>{r.overallScore}</span>
              </div>
              {r.summary && <p className="text-sm text-ink-muted mb-4 leading-relaxed" style={{fontWeight:300}}>{r.summary}</p>}
              {sortedAttrs.length > 0 && (
                <div className="space-y-2 mb-4">
                  {sortedAttrs.map(([name, val]) => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="text-[11px] font-sans text-ink-muted w-24 capitalize" style={{fontWeight:300}}>{name}</span>
                      <div className="flex-1 h-[3px] bg-cream-dark rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${val >= 7 ? 'bg-emerald-400' : val >= 5 ? 'bg-blue-400' : 'bg-amber-400'}`} style={{ width: `${(val / 10) * 100}%` }} />
                      </div>
                      <span className="text-[11px] font-sans text-ink-muted w-6 text-right" style={{fontWeight:500}}>{val.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              )}
              {r.traits.length > 0 && <div className="flex flex-wrap gap-1.5">{r.traits.map((t: string) => <span key={t} className="text-[10px] font-sans text-ink-muted bg-cream-warm px-2 py-0.5 rounded">{t}</span>)}</div>}
            </div>
          );
        })}
      </div>

      {/* What to do next */}
      <div className="card mb-10">
        <h2 className="text-sm font-sans font-medium text-ink-muted mb-4">What to do next</h2>
        {composite >= 80 ? (
          <div>
            <p className="text-sm text-ink-muted mb-4" style={{fontWeight:300}}>
              This is an excellent photo. It&apos;s performing in the top tier across all three AI models.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => downloadShareCard('1:1')} className="btn-rose text-xs">Download share card</button>
              <button onClick={() => navigate('/app')} className="btn-secondary text-xs">
                {projectVertical === 'linkedin' ? 'Compare headshot options' : 'Compare with your lineup'}
              </button>
            </div>
          </div>
        ) : composite >= 60 ? (
          <div>
            <p className="text-sm text-ink-muted mb-4" style={{fontWeight:300}}>
              Good photo — but there&apos;s room to push this higher. Upload a few alternatives and compare them side by side.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/app')} className="btn-rose text-xs">
                {projectVertical === 'linkedin' ? 'Upload another headshot' : 'Upload another dating photo'}
              </button>
              <button onClick={() => {
                const photosForContext = project?.photoIds.length ?? 0;
                import('../lib/analytics').then(({ trackCompareStarted }) => trackCompareStarted(photosForContext, context));
                navigate('/compare');
              }} className="btn-secondary text-xs">
                {projectVertical === 'linkedin' ? 'Compare headshot options' : 'Compare with your lineup'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-ink-muted mb-4" style={{fontWeight:300}}>
              This photo has potential, but a different shot could score significantly higher. Most people see a 15-20 point jump on their second try.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/app')} className="btn-rose text-xs">
                {projectVertical === 'linkedin' ? 'Upload another headshot' : 'Upload another dating photo'}
              </button>
              {blogLink && (
                <Link to={`/blog/${blogLink.slug}`} className="btn-secondary text-xs">
                  Read: {blogLink.title}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contextual blog link (Phase 5) */}
      {blogLink && (
        <div className="card-hover mb-10 p-5">
          <Link to={`/blog/${blogLink.slug}`} className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-rose-faint flex items-center justify-center text-rose text-sm shrink-0">
              {context === 'dating' ? '💕' : context === 'business' ? '💼' : '🎉'}
            </div>
            <div className="flex-1">
              <div className="text-sm font-sans text-ink-muted font-medium mb-0.5">Recommended reading</div>
              <div className="text-sm font-sans font-medium text-ink group-hover:text-rose">{blogLink.title}</div>
            </div>
            <span className="text-xs text-rose">→</span>
          </Link>
        </div>
      )}

      {/* Share your score */}
      <div className="card-rose mb-10 text-center p-8">
        <div className="text-2xl mb-3">&#x1F525;</div>
        <h3 className="font-sans text-xl text-ink mb-2" style={{fontWeight: 600}}>
          You scored {composite}. Can your friend beat it?
        </h3>
        <p className="text-xs text-ink-faint mb-5" style={{fontWeight: 300}}>
          Share your score and see if a friend can top it.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={() => {
            import('../lib/analytics').then(({ trackShareClicked }) => trackShareClicked('score'));
            const shareData = {
              title: 'PhotoFeeler AI Challenge',
              text: `I scored ${composite}/100 on my ${ctxLabel} photo! Can you beat it?`,
              url: `${window.location.origin}/challenge/new?iq=${composite}&context=${projectVertical}&name=${encodeURIComponent('A friend')}`,
            };
            if (navigator.share) {
              navigator.share(shareData).catch(() => {});
            } else {
              navigator.clipboard.writeText(shareData.url).then(() => showToast('Link copied!', 'success')).catch(() => showToast('Couldn\u0027t copy \u2014 try again or copy manually.', 'error'));
            }
          }} className="btn-rose text-sm">
            Share your score
          </button>
          <button onClick={() => {
            import('../lib/analytics').then(({ trackShareClicked }) => trackShareClicked('quiz'));
            const guessUrl = `${window.location.origin}/guess/${composite}-${Math.max(20, composite - 15 + Math.floor(Math.random() * 30))}-${context}`;
            const shareData = {
              title: 'Which photo scored higher?',
              text: 'Can you guess which photo the AI rated higher? Try it!',
              url: guessUrl,
            };
            if (navigator.share) {
              navigator.share(shareData).catch(() => {});
            } else {
              navigator.clipboard.writeText(guessUrl).then(() => showToast('Guess link copied!', 'success')).catch(() => showToast('Couldn\u0027t copy \u2014 try again or copy manually.', 'error'));
            }
          }} className="btn-secondary text-sm">
            Share photo quiz
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center gap-3">
        <button onClick={() => navigate('/app')} className="btn-rose text-sm w-full max-w-xs">{projectVertical === 'linkedin' ? 'Upload another headshot' : 'Upload another dating photo'}</button>
        <div className="flex justify-center gap-3">
          <button onClick={() => downloadShareCard('1:1')} className="btn-secondary text-xs">Share card</button>
          <button onClick={() => downloadShareCard('9:16')} className="btn-ghost text-xs">Stories format</button>
          <button onClick={() => {
            const text = `I scored ${composite}/100 on my ${ctxLabel} photo! Rated by ${modelCount} AI models on PhotoFeeler AI`;
            navigator.clipboard.writeText(text).then(() => showToast('Copied to clipboard!', 'success')).catch(() => showToast('Couldn\u0027t copy \u2014 try again or copy manually.', 'error'));
          }} className="btn-ghost text-xs">Copy text</button>
        </div>
      </div>
    </div>
  );
}

// Animated score counter for the reveal
function ScoreCounter({ target, className }: { target: number; className?: string }) {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setN(0);
    let current = 0;
    const duration = 1200;
    const steps = 30;
    const increment = target / steps;
    const stepTime = duration / steps;

    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        current += increment;
        if (current >= target) {
          setN(target);
          clearInterval(interval);
        } else {
          setN(Math.round(current));
        }
      }, stepTime);
    }, 300);

    return () => clearTimeout(timer);
  }, [target]);

  return (
    <div ref={ref} className={className} style={{ fontWeight: 600, letterSpacing: '-0.04em' }}>
      {n}
    </div>
  );
}

function getVerticalVerdict({
  vertical,
  score,
  rankingPosition,
}: {
  vertical: ProfileVertical;
  score: number;
  rankingPosition: RankingSequenceItem | null;
}) {
  if (vertical === 'linkedin') {
    if (rankingPosition?.position === 1) {
      return {
        title: 'This is your current primary headshot.',
        detail:
          'It is the strongest recruiter-facing option in your latest shortlist. Keep it as the main LinkedIn photo and hold the next option as backup.',
      };
    }

    if (rankingPosition) {
      return {
        title: 'Keep this as a backup, not the main headshot.',
        detail:
          'This photo can support future refreshes, team pages, or alternate surfaces, but another headshot is currently winning the first-impression test.',
      };
    }

    if (score >= 80) {
      return {
        title: 'Strong candidate for your LinkedIn headshot.',
        detail:
          'The score is already strong enough to use, but a side-by-side shortlist will tell you whether this should stay primary or become the backup.',
      };
    }

    return {
      title: 'Usable, but still worth testing against alternatives.',
      detail:
        'This photo can work, but LinkedIn benefits from a clear primary headshot choice. Compare a few options before you lock it in.',
    };
  }

  if (rankingPosition?.position === 1) {
    return {
      title: 'This is your current lead-photo favorite.',
      detail:
        'The latest lineup puts this photo first. Use it in slot one, then let the next photos add variety without replacing the opener.',
    };
  }

  if (rankingPosition) {
    return {
      title: 'Better later in the lineup than in slot one.',
      detail:
        'This photo still helps the profile, but the latest ranking suggests another image makes the stronger first impression.',
    };
  }

  if (score >= 80) {
    return {
      title: 'Strong candidate for your first dating photo.',
      detail:
        'The score is high enough to test as a lead photo, but a ranked set will tell you whether it truly deserves the first slot or works better later.',
    };
  }

  return {
    title: 'Potentially useful, but not yet a locked-in opener.',
    detail:
      'This photo may still belong in the profile, but the right dating lineup needs a stronger read on which image should open first.',
  };
}
